/**
 * AUTO-BOT Type Definitions
 *
 * Automated trading system that maps strategies to broker accounts
 * for hands-free signal execution with configurable risk controls.
 */

// ============================================================================
// Core Bot Configuration
// ============================================================================

/**
 * Classic strategy-signal bot (WholeCandleStrategy, etc.).
 * Discriminated from BreakOfStructureBot via the optional `signalSource` tag
 * (absent / 'strategy' => classic). This preserves all existing bots which
 * were persisted without a `signalSource` field.
 */
export interface ClassicAutoBot {
  id: string;                      // UUID
  name: string;                    // User-friendly name
  enabled: boolean;                // Master enable/disable
  createdAt: number;               // Timestamp
  updatedAt: number;               // Timestamp

  // Discriminant (optional for back-compat; undefined === classic)
  signalSource?: 'strategy';

  // Signal Source
  strategyName: string;            // e.g., "WholeCandleStrategy"
  symbol: string;                  // e.g., "MNQH6" (or "*" for any)
  signalFilter: SignalFilter;      // Which signals to act on

  // Target Accounts
  targets: BotTarget[];            // One or more execution targets

  // Risk Management
  riskConfig: BotRiskConfig;       // Limits and controls

  // Execution Settings
  executionConfig: BotExecutionConfig;

  /**
   * Per-score-band rules. When non-empty these SUPERSEDE `signalFilter`'s
   * confidence band and `executionConfig`'s entry terms.
   *
   * Mirrors `ScoreSlice` in utt-engine/src/auto_bot/slices.rs. Two bots that
   * differ only in band and entry style are one bot with two rules; splitting
   * them duplicated symbol, account, size, TP, blackout and risk limits, and
   * left `cancelRestingOnSignal` expressing a rule BETWEEN two configs that
   * could not see each other.
   */
  slices?: ScoreSlice[];

  /** What to do when a signal arrives while already positioned. */
  positionPolicy?: PositionPolicy;

  /**
   * How one signal is spread across `targets`. Mirrors `SelectionMode` in
   * utt-engine/src/auto_bot/types.rs.
   *
   * Optional on purpose: every bot persisted before this field existed has no
   * such key, and the engine's `#[serde(default)]` reads that absence as
   * `fanout` — the original behaviour. Never write `fanout` just to be
   * explicit; leaving it off and leaving it 'fanout' mean the same thing.
   */
  selectionMode?: SelectionMode;

  /** Bot ids this one was migrated from, for reconciling against a backup. */
  migratedFrom?: string[];

  /**
   * Local (America/Los_Angeles) hour at which any position this bot's targets
   * still hold is closed at market. 0-23. Mirrors `flatten_hour` in
   * utt-engine/src/auto_bot/types.rs.
   *
   * Three states, not two, and the distinction is what the PATCH merge turns
   * on: a number sets the hour, an explicit `null` CLEARS it, and the key being
   * absent leaves whatever the bot already had. The engine's merge arm is
   * `if let Some(hour) = updates.get("flattenHour")`, so omitting the key on a
   * bot that carries hour 13 keeps 13 — sending `null` is the only way to turn
   * the feature back off.
   *
   * Every bot persisted before this field existed has no such key and
   * deserializes to `None`, which is no time-based close at all. That default
   * must stay: a defaulted hour would start closing positions on bots whose
   * operator never asked for one.
   */
  flattenHour?: number | null;

  // Runtime State (not persisted in config, loaded separately)
  state?: BotRuntimeState;
}

/** How a slice gets into the market. Mirrors `SliceEntry` in Rust. */
export type SliceEntry =
  | { kind: 'chase' }
  | { kind: 'pullback'; offsetPoints: number; expirySeconds: number };

/** One score band and how to trade it. Mirrors `ScoreSlice` in Rust. */
export interface ScoreSlice {
  id: string;
  /** Shown in the editor and on execution records, so a fill traces back to
   *  the rule that placed it. */
  label: string;
  /** Inclusive. */
  minScore: number;
  /** EXCLUSIVE, or null for "no ceiling" — so bands abut without overlapping
   *  and a score on the boundary belongs to the upper slice only. */
  maxScore: number | null;
  entry: SliceEntry;
  enabled: boolean;
  sizeOverride?: number | null;
  tpOverride?: number | null;
  slOverride?: number | null;
}

/**
 * What the bot does when a signal arrives while it is already positioned.
 *
 * `martingale` SATURATES at `maxMultiplier` rather than pausing: a bot that
 * stops trading at the cap can never get the win that would reset it.
 */
export type PositionPolicy =
  | { kind: 'skip' }
  | { kind: 'flip' }
  | { kind: 'martingale'; maxMultiplier: number };

// ============================================================================
// Break-of-Structure (BOS / CHoCH) Bot
// ============================================================================

export type BosTriggerMode = 'instant' | 'confirmed';
export type BosGrade = 'A+' | 'A' | 'B+' | 'B';
export type StructureType = 'bos' | 'choch';
export type BosSlMode = 'off' | 'night' | 'all';

export interface BosSlWindow {
  start: string;                   // "22:00" (24h, tz-local)
  end: string;                     // "07:00"
  tz: string;                      // IANA tz, e.g. "America/Los_Angeles"
}

/**
 * BOS/CHoCH auto-bot. Fixed contract shape consumed by tradovate-service.
 * Note: `positionMode` and `circuitBreaker` are single-valued by contract.
 */
export interface BreakOfStructureBot {
  id: string;                      // UUID
  name: string;                    // User-friendly name
  enabled: boolean;                // Master enable/disable
  createdAt: number;               // Timestamp
  updatedAt: number;               // Timestamp

  // Discriminant
  signalSource: 'break_of_structure';

  symbol: string;                  // e.g. "MNQ"
  triggerMode: BosTriggerMode;     // instant vs candle-completion (confirmed)
  minGrade: BosGrade;              // minimum break grade to act on
  structureTypes: StructureType[]; // ('bos' | 'choch')[]
  quantity: number;                // contracts per entry
  tpPoints: number;                // take-profit (points)
  slPoints: number;                // stop-loss (points)
  slMode: BosSlMode;               // off / night-only / all-hours
  slWindow: BosSlWindow;           // active window when slMode === 'night'
  maxHoldMinutes: number;          // timed exit (0 = off)
  positionMode: 'skip_if_positioned';
  circuitBreaker: 'stop_after_loss' | 'none'; // 'none' = no halt on loss (default)

  // Runtime State (not persisted in config, loaded separately)
  state?: BotRuntimeState;
}

/**
 * A bot is either a classic strategy bot or a break-of-structure bot,
 * discriminated on `signalSource`.
 */
export type AutoBot = ClassicAutoBot | BreakOfStructureBot;

/**
 * Distributive Omit — plain `Omit<Union, K>` collapses a union to its common
 * keys, which would erase the discriminated members' distinct fields. This
 * variant distributes over the union so each member keeps its own shape.
 */
export type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;

/** Type guard: narrow an AutoBot to a BreakOfStructureBot. */
export function isBreakOfStructureBot(bot: AutoBot): bot is BreakOfStructureBot {
  return bot.signalSource === 'break_of_structure';
}

/**
 * ATR floor the ENGINE applies when `skipLowAtr` is on and `minAtr` is unset.
 * Mirrors `DEFAULT_BOT_MIN_ATR` in utt-engine/src/auto_bot/types.rs — if that
 * moves, move this.
 */
export const DEFAULT_BOT_MIN_ATR = 12;

/**
 * The STRATEGY's own ATR floor (`SetupCandidateSettings::min_atr`), shown in the
 * editor purely to explain why the bot floor is a different, higher number.
 */
export const STRATEGY_MIN_ATR = 8;

export interface SignalFilter {
  actions: SignalAction[];         // Filter by direction
  minConfidence?: number;          // Min confidence score (0-100)

  /**
   * Upper bound on the score, EXCLUSIVE. 55 takes 54 and leaves 55 to whatever
   * bot sits above, so two bands can abut without both firing on one bar.
   *
   * Undefined means no ceiling. Never send 0 to mean "no ceiling" — a ceiling
   * of 0 rejects every signal and the bot goes quietly dead.
   */
  maxConfidence?: number;
  timeWindow?: TimeWindow;         // Only trade during certain hours
  /**
   * Skip signals whose ATR is below `minAtr`.
   *
   * Spelled `skipLowAtr`, NOT `skipLowATR`. The engine derives the wire name
   * from `skip_low_atr` via serde's camelCase rename, which produces a lowercase
   * `tr`. This was `skipLowATR` for its whole life, so the editor wrote a key
   * the engine did not recognise, serde dropped it, and the checkbox silently
   * did nothing.
   */
  skipLowAtr?: boolean;

  /**
   * ATR floor in points, applied only when `skipLowAtr` is on.
   *
   * Distinct from the STRATEGY's own `minAtr` (8.0), which decides whether a
   * diamond is detected at all. This decides whether the bot acts on one that
   * was — so a diamond can print on the chart and still be sat out.
   *
   * Undefined means the engine's default (12).
   */
  minAtr?: number;

  skipNYSessionEdges?: boolean;    // Skip first/last 15 mins of NY RTH (9:30-9:45, 15:45-16:00 ET)

  /**
   * Periods during which NO new entry is taken. Exclusion, not inclusion —
   * `timeWindow` says when the bot MAY trade, this says when it may not, and
   * a blackout wins over the trading window.
   *
   * Blocks entries only. Nothing here ever closes an open position; exits are
   * the operator's business.
   *
   * Optional and defaulted to absent on purpose: every bot already persisted
   * predates this field and must keep behaving exactly as it did.
   *
   * Wrap-aware in the engine: 22:00-02:00 spans midnight, and such a window is
   * attributed to the day it STARTS on. Boundaries are inclusive at both ends.
   */
  noEntryWindows?: TimeWindow[];
}

export interface TimeWindow {
  enabled: boolean;
  startTime: string;               // "09:30" (CT)
  endTime: string;                 // "16:00" (CT)
  tradeDays: number[];             // [1,2,3,4,5] = Mon-Fri (1=Monday)
}

export type SignalAction = 'BUY' | 'SELL';

// ============================================================================
// Target Configuration
// ============================================================================

export interface BotTarget {
  id: string;                      // UUID
  enabled: boolean;                // Per-target enable
  brokerId: string;                // e.g., "mffu"
  accountId: number;               // e.g., 35189314
  accountName?: string;            // Display name cache

  // Position Sizing
  sizeMode: SizeMode;
  fixedSize?: number;              // For 'fixed' mode
  scaleMultiplier?: number;        // For 'scaled' mode (e.g., 2.0 = 2x)
  accountPercent?: number;         // For 'account_percent' mode
  maxPositionSize: number;         // Hard cap regardless of mode

  // Position Handling is not configurable: a target that already holds a
  // position sits the signal out, full stop. The only exemption is a
  // bot-level `positionPolicy` reversal, which replaces the position rather
  // than opening a second one. Old persisted targets still carry
  // `positionMode`; the engine ignores it.

  // TP/SL Configuration
  tpslMode: TPSLMode;
  // Read verbatim by BOTH 'fixed' and 'none' — the engine's
  // resolve_bracket_points returns this pair unchanged for either mode.
  // Absent means the leg is not attached at all, which is why they are
  // optional rather than defaulted.
  fixedTPPoints?: number;
  fixedSLPoints?: number;
  /**
   * @deprecated Wrong case for the Rust engine, and a different quantity.
   *
   * These two are read by `tradovate-service`'s legacy
   * `AutoBotExecutionEngine`, where they multiply ATR. The Rust engine that
   * actually serves `PATCH /auto-bot/:id` today declares
   * `dynamic_tp_multiplier` under `#[serde(rename_all = "camelCase")]` with no
   * explicit rename, so its wire key is `dynamicTpMultiplier` — lowercase `p`.
   * `fixedTPPoints` right above only keeps its capitals because Rust spells it
   * out in a `#[serde(rename)]`; these never got one.
   *
   * Writing the capitalised name at the Rust engine is silent: serde ignores
   * the unknown key, both multipliers stay `None`, and a `dynamic` target with
   * no usable leg is refused by `bracket_refusal` on every signal it sees.
   * Use `dynamicTpMultiplier` / `dynamicSlMultiplier` below.
   */
  dynamicTPMultiplier?: number;
  /** @deprecated See `dynamicTPMultiplier`. */
  dynamicSLMultiplier?: number;

  /**
   * `tpslMode: 'dynamic'` only: take profit = this MANY TIMES the signal's
   * `parentRange`, in points. A bare multiplier, not a point or tick count —
   * `1.0` against a 20-point parent range is a 20-point target.
   *
   * Only diamonds carry a `parentRange`. A WCC signal has none, so a dynamic
   * target refuses the entry outright rather than placing it unbracketed.
   *
   * Absent means the leg is not attached, which is a supported configuration
   * as long as the other multiplier names one — the engine's rule is "at least
   * one exit", not "a stop". `0`, a negative and `NaN` all count as absent for
   * that test: they are not "no leg", they are a leg zero points away.
   */
  dynamicTpMultiplier?: number;
  /** `tpslMode: 'dynamic'` only: stop loss = this x the signal's `parentRange`,
   *  in points. See `dynamicTpMultiplier` for why absent is legal. */
  dynamicSlMultiplier?: number;

  // Bracket Order Settings
  useBracketOrder: boolean;        // Send as bracket (OCO) order
  trailingStop: boolean;           // Use trailing stop (future)

  /**
   * Trade the OPPOSITE side of every signal this target receives: a BUY
   * signal places a SELL, a SELL places a BUY.
   *
   * Per TARGET, not per bot, so a straight account and a faded one can run
   * side by side under one bot on exactly the same signals.
   *
   * Absent means OFF, and the engine defaults it that way — every target
   * stored before this field existed keeps behaving as it did. It does NOT
   * change which signals the bot takes: the signal filters (`actions`,
   * `requireStackAligned`, `maxTrendR2`) all judge the signal's own
   * direction, and this only decides which side it is traded on.
   *
   * The editor writes `true` or nothing — never `false`. An explicit `false`
   * and an absent key mean the same thing to the engine, but the whole-array
   * `targets` replace makes the difference visible in Redis, and a target that
   * has never opted in should read as one that has never opted in.
   */
  invertSide?: boolean;
}

/**
 * How one signal is distributed over a bot's target accounts.
 *
 * - `fanout` — every enabled target takes every signal. The default, and what
 *   every pre-existing bot does.
 * - `first_eligible` — the signal goes to ONE account: the first target in
 *   `targets` that is open (not positioned, position known, not already
 *   claimed). The ORDER of `targets` is the routing priority, so reordering
 *   the list changes which prop firm takes the next real trade.
 */
export type SelectionMode = 'fanout' | 'first_eligible';

export type SizeMode = 'fixed' | 'scaled' | 'account_percent';

/**
 * How a target sizes its bracket legs.
 *
 * - `fixed` — `fixedTPPoints` / `fixedSLPoints`, verbatim. The engine REFUSES
 *   an entry whose SL is unset in this mode: picking `fixed` is the operator
 *   asserting a stop exists.
 * - `dynamic` — multiples of the signal's parent range.
 * - `none` — no managed stop POLICY, not "no bracket". It still attaches
 *   whatever `fixedTPPoints` / `fixedSLPoints` are set, and carries no
 *   refusal, so it is the only way to express take-profit-only. The string
 *   is persisted in Redis and parsed by the Rust `TPSLMode` enum; the UI
 *   label may change, this value may not.
 */
export type TPSLMode = 'fixed' | 'dynamic' | 'none';

// ============================================================================
// Risk Configuration
// ============================================================================

export interface BotRiskConfig {
  // Daily Limits
  maxDailyLoss: number;            // Max $ loss per day (0 = unlimited)
  maxDailyTrades: number;          // Max trades per day (0 = unlimited)
  maxDailyProfit?: number;         // Stop after hitting profit target (0 = unlimited)

  /**
   * Position Limits
   *
   * No concurrency cap on purpose. Contracts are capped per target by
   * `maxPositionSize`, and one position per account is enforced
   * unconditionally by the engine, so concurrency is just the number of
   * enabled targets. Old persisted configs still carry
   * `maxConcurrentPositions`; the engine ignores it.
   */
  maxPositionSize: number;         // Max contracts per position

  // Circuit Breaker
  circuitBreaker: CircuitBreakerConfig;

  // Cooldown
  cooldownSeconds: number;         // Min seconds between trades (0 = none)

  // Market Hours
  respectMarketHours: boolean;     // Only trade during CME market hours
  respectRTHOnly: boolean;         // Only trade during RTH session
}

export interface CircuitBreakerConfig {
  enabled: boolean;
  consecutiveLosses: number;       // Pause after N consecutive losses
  pauseDurationMinutes: number;    // How long to pause
}

// ============================================================================
// Execution Configuration
// ============================================================================

export interface BotExecutionConfig {
  // Timing
  executionDelay: number;          // Delay in ms before executing (0 = immediate)
  retryOnFailure: boolean;         // Retry failed orders
  maxRetries: number;              // Max retry attempts
  retryDelayMs: number;            // Delay between retries

  // Order Type
  orderType: OrderType;            // Market or limit order

  /**
   * Dead. Kept only so bots persisted with the key still parse.
   *
   * It was in the editor and on every stored bot, but nothing ever read it —
   * the engine never sent it and a bot set to Limit still went to market.
   * Superseded by `limitOffsetPoints`.
   *
   * @deprecated use limitOffsetPoints
   */
  limitOffsetTicks?: number;

  /**
   * How far BETTER than the signal price to rest the entry, in points.
   *
   * Direction is derived from the action, never configured: a buy rests below
   * the signal price and a sell above it, so a positive number can never be a
   * chase.
   */
  limitOffsetPoints?: number;

  /**
   * Cancel the resting entry if unfilled after this long.
   *
   * A timer in tradovate-service rather than an order property, because
   * Tradovate's time-in-force is Day or GTC with no GTD. Undefined or 0 leaves
   * the order until the session ends.
   */
  entryExpirySeconds?: number;

  /**
   * Before placing, cancel every resting entry on the same account and symbol —
   * left by another bot OR by this bot's own earlier signal — and wait for the
   * cancel to be confirmed before entering.
   *
   * Supersede semantics: the newest signal wins. It used to spare this bot's
   * own orders, which is how #369 doubled a live position: the position gate
   * reads netPos, an unfilled resting limit does not move netPos, and the one
   * order that could not be cancelled was the one in the way.
   *
   * A cancel that times out or fails REFUSES the entry rather than placing on
   * top of an order that may still be working.
   */
  cancelRestingOnSignal?: boolean;

  // Confirmation
  requireConfirmation: boolean;    // Show confirmation before executing
  confirmationTimeoutSeconds: number; // Auto-execute after timeout if no response
}

export type OrderType = 'market' | 'limit';

// ============================================================================
// Runtime State
// ============================================================================

export interface BotRuntimeState {
  status: BotStatus;
  pausedAt?: number;
  pauseReason?: string;
  resumeAt?: number;               // For circuit breaker auto-resume

  // Daily Stats (reset at session start)
  dailyStats: DailyStats;

  // Current Positions (tracked per target)
  openPositions: Record<string, number>; // targetId → position size
}

export type BotStatus = 'active' | 'paused' | 'stopped' | 'circuit_breaker';

export interface DailyStats {
  date: string;                    // "2025-12-30"
  trades: number;
  wins: number;
  losses: number;
  grossPnL: number;
  netPnL: number;
  consecutiveLosses: number;
  lastTradeTime?: number;
}

// ============================================================================
// Execution Records
// ============================================================================

export interface AutoBotExecution {
  id: string;                      // UUID
  botId: string;                   // Parent bot
  botName: string;                 // Cached for display
  timestamp: number;               // Execution time

  // Signal Info
  signalId: string;                // Original signal ID
  signalAction: SignalAction;      // What the STRATEGY signalled (see `inverted`)
  signalPrice: number;
  signalTimestamp: number;
  strategyName: string;

  // Execution Details
  targetId: string;
  brokerId: string;
  accountId: number;
  accountName?: string;

  // Order Details
  action: 'Buy' | 'Sell';          // The side actually SENT to the broker
  /**
   * The target inverted this signal, so `action` is the opposite of
   * `signalAction`. Absent on every execution recorded before the flag
   * existed. Without this a Sell against a bullish diamond reads as a bug.
   */
  inverted?: boolean;
  symbol: string;
  quantity: number;
  orderType: 'Market' | 'Limit';
  limitPrice?: number;

  // Bracket Details (if applicable)
  usedBracket: boolean;
  tpPrice?: number;
  slPrice?: number;

  // Result
  status: ExecutionStatus;
  orderId?: string;                // Broker order ID
  fillPrice?: number;
  fillTime?: number;
  error?: string;

  // Metrics
  latencyMs: number;               // Time from signal to order placed

  // P&L (filled after position closed)
  pnl?: number;
  pnlPoints?: number;
}

export type ExecutionStatus = 'pending' | 'success' | 'failed' | 'rejected' | 'skipped';

// ============================================================================
// Position Action (internal use)
// ============================================================================

export type PositionAction =
  | { action: 'skip'; reason: string }
  | { action: 'execute'; quantity: number }
  | { action: 'flatten_then_execute'; flattenFirst: true; quantity: number };

// ============================================================================
// API Types
// ============================================================================

export interface AutoBotStatus {
  globalEnabled: boolean;
  activeBots: number;
  totalBots: number;
  dailyStats: {
    totalTrades: number;
    totalPnL: number;
    winRate: number;
  };
}

export interface CreateBotRequest {
  name: string;
  strategyName: string;
  symbol: string;
  signalFilter?: Partial<SignalFilter>;
  targets: Omit<BotTarget, 'id'>[];
  riskConfig?: Partial<BotRiskConfig>;
  executionConfig?: Partial<BotExecutionConfig>;
}

export interface UpdateBotRequest {
  botId: string;
  updates: Partial<Omit<AutoBot, 'id' | 'createdAt' | 'state'>>;
}

// ============================================================================
// Default Configurations
// ============================================================================

export const DEFAULT_SIGNAL_FILTER: SignalFilter = {
  actions: ['BUY', 'SELL'],
  timeWindow: {
    enabled: false,
    startTime: '09:30',
    endTime: '16:00',
    tradeDays: [1, 2, 3, 4, 5],
  },
  // Was `skipLowATR`, which the engine never recognised — see the field docs.
  // Left ON as the historical intent, but note this only starts taking effect
  // now that the key is spelled correctly, so a bot created from these defaults
  // will begin refusing sub-`minAtr` signals where before it took them all.
  skipLowAtr: true,
  minAtr: DEFAULT_BOT_MIN_ATR,
  skipNYSessionEdges: true, // Default: enabled
};

export const DEFAULT_TARGET_CONFIG: Omit<BotTarget, 'id' | 'brokerId' | 'accountId'> = {
  enabled: true,
  sizeMode: 'fixed',
  fixedSize: 1,
  maxPositionSize: 5,
  tpslMode: 'fixed',
  fixedTPPoints: 5,
  fixedSLPoints: 50,
  useBracketOrder: true,
  trailingStop: false,
};

export const DEFAULT_RISK_CONFIG: BotRiskConfig = {
  maxDailyLoss: 500,
  maxDailyTrades: 0, // 0 = unlimited
  maxDailyProfit: 0,
  maxPositionSize: 5,
  circuitBreaker: {
    enabled: true,
    consecutiveLosses: 3,
    pauseDurationMinutes: 30,
  },
  cooldownSeconds: 60,
  respectMarketHours: true,
  respectRTHOnly: false,
};

export const DEFAULT_EXECUTION_CONFIG: BotExecutionConfig = {
  executionDelay: 0,
  retryOnFailure: false,
  maxRetries: 2,
  retryDelayMs: 1000,
  orderType: 'market',
  requireConfirmation: false,
  confirmationTimeoutSeconds: 30,
};

/**
 * BOS/CHoCH bot defaults (from backtest).
 */
export const DEFAULT_BOS_SL_WINDOW: BosSlWindow = {
  start: '22:00',
  end: '07:00',
  tz: 'America/Los_Angeles',
};

export const DEFAULT_BOS_BOT: DistributiveOmit<
  BreakOfStructureBot,
  'id' | 'createdAt' | 'updatedAt' | 'state'
> = {
  name: 'BOS/CHoCH Bot',
  enabled: false,
  signalSource: 'break_of_structure',
  symbol: 'MNQ',
  triggerMode: 'instant',
  minGrade: 'B',
  structureTypes: ['bos', 'choch'],
  quantity: 1,
  tpPoints: 10,
  slPoints: 40,
  slMode: 'all',
  slWindow: { ...DEFAULT_BOS_SL_WINDOW },
  maxHoldMinutes: 0,
  positionMode: 'skip_if_positioned',
  circuitBreaker: 'none', // default OFF — bot keeps trading through losses unless toggled on
};

export const DEFAULT_DAILY_STATS: DailyStats = {
  date: new Date().toISOString().split('T')[0],
  trades: 0,
  wins: 0,
  losses: 0,
  grossPnL: 0,
  netPnL: 0,
  consecutiveLosses: 0,
};

// ============================================================================
// Utility Functions
// ============================================================================

export function createDefaultBot(partial: Partial<ClassicAutoBot>): ClassicAutoBot {
  const now = Date.now();
  return {
    id: `bot_${now}_${Math.random().toString(36).substr(2, 9)}`,
    name: partial.name || 'New Bot',
    enabled: false,
    createdAt: now,
    updatedAt: now,
    strategyName: partial.strategyName || 'WholeCandleStrategy',
    symbol: partial.symbol || '*',
    signalFilter: { ...DEFAULT_SIGNAL_FILTER, ...partial.signalFilter },
    targets: partial.targets || [],
    riskConfig: { ...DEFAULT_RISK_CONFIG, ...partial.riskConfig },
    executionConfig: { ...DEFAULT_EXECUTION_CONFIG, ...partial.executionConfig },
  };
}

export function createDefaultTarget(brokerId: string, accountId: number, accountName?: string): BotTarget {
  return {
    id: `target_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    brokerId,
    accountId,
    accountName,
    ...DEFAULT_TARGET_CONFIG,
  };
}

export function createDefaultRuntimeState(): BotRuntimeState {
  return {
    status: 'stopped',
    dailyStats: { ...DEFAULT_DAILY_STATS },
    openPositions: {},
  };
}
